
import { useState, useContext, useEffect } from "react"
import "./App.css"
import MathPlatformIntro from "./components/MathPlatformIntro"
import { AuthContext } from "./components/AuthContext"
import LatexGenerator from "./components/topicSelectorComponents/LectureNotesGenerator"
import SlidesGenerator from "./components/topicSelectorComponents/SlidesGenerator"

import {
    FaGraduationCap,
    FaChartLine,
    FaComments,
    FaCalculator,
    FaCheckSquare,
    FaSearch,
    FaFolderOpen,
    FaEdit,
    FaFilePowerpoint,
    FaFileImage,
    FaSuperscript,
    FaAsterisk,
    FaSquareRootAlt,
    FaFilePdf,
    FaStar,
    FaRocket,
    FaBrain,
} from "react-icons/fa"
import { useNavigate } from "react-router-dom"
import { Helmet } from "react-helmet"

function Home() {
    const [query, setQuery] = useState("")
    const [suggestedLink, setSuggestedLink] = useState(null)
    const [loading, setLoading] = useState(false)
    const [isVisible, setIsVisible] = useState(false)
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
    const navigate = useNavigate()
    const { isGuest, user } = useContext(AuthContext)
    const [showLecturePopup, setShowLecturePopup] = useState(false)
    const [showSlidesPopup, setSlidesPopup] = useState(false)

    // Animation effects
    useEffect(() => {
        setIsVisible(true)

        const handleMouseMove = (e) => {
            setMousePosition({ x: e.clientX, y: e.clientY })
        }

        window.addEventListener("mousemove", handleMouseMove)
        return () => window.removeEventListener("mousemove", handleMouseMove)
    }, [])

    // Main functions - 2x2 grid, largest cards
    const mainFunctions = [
        {
            name: "Chatbox",
            path: "/chatbox",
            icon: FaComments,
            gradient: "from-blue-500 via-indigo-500 to-red-500",
            description: "AI-powered instant help for all your questions",
        },
        {
            name: "Past Papers",
            path: "/past-papers",
            icon: FaFolderOpen,
            gradient: "from-emerald-500 via-teal-500 to-cyan-500",
            description: "Access comprehensive exam archives",
        },
        {
            name: "Exam Grader",
            path: "/exam-grader",
            icon: FaCheckSquare,
            gradient: "from-orange-500 via-red-500 to-red-500",
            description: "Automated assessment and feedback",
        },
        {
            name: "Quiz Generator",
            path: "/quiz-generator",
            icon: FaSearch,
            gradient: "from-violet-500 via-indigo-500 to-indigo-500",
            description: "Create personalized practice tests",
        },
    ]

    // Advanced tools - medium sized cards
    const advancedTools = [
        {
            name: "Plot Generator",
            icon: FaChartLine,
            link: "/plot-generator",
            gradient: "from-blue-500 to-cyan-500",
            description: "Generate mathematical plots and graphs",
        },
        {
            name: "Notes Generation",
            icon: FaEdit,
            onClick: () => setShowLecturePopup(true),
            gradient: "from-green-500 to-emerald-500",
            description: "Generate detailed notes from lectures",
        },
        {
            name: "Slides Generation",
            icon: FaFilePowerpoint,
            onClick: () => setSlidesPopup(true),
            gradient: "from-red-500 to-red-500",
            description: "Create presentation slides using AI",
        },
        {
            name: "Calculator",
            icon: FaCalculator,
            link: "/calculator",
            gradient: "from-indigo-500 to-indigo-500",
            description: "Advanced mathematical calculator",
        },
    ]

    // Utility functions - smallest boxes
    const utilityFunctions = [
        {
            id: "course-helper",
            name: "Course Helper",
            icon: FaGraduationCap,
            link: "/topic-selector",
        },
        {
            id: "math-papers",
            name: "Math Papers",
            icon: FaFolderOpen,
            link: "/google-drive",
        },
        {
            id: "image-to-text",
            name: "Image to Text",
            icon: FaFileImage,
            link: "/image-text-convertor",
        },
        {
            id: "compile-latex",
            name: "Compile Latex",
            icon: FaSuperscript,
            link: "/latex-editor",
        },
        {
            id: "knowledge-galaxy",
            name: "Knowledge Galaxy",
            icon: FaAsterisk,
            link: "/knowledge-network",
        },
        {
            id: "formula-search",
            name: "Formula Search",
            icon: FaSquareRootAlt,
            link: "/formula-search",
        },
        {
            id: "question-search",
            name: "Question Search",
            icon: FaSquareRootAlt,
            link: "/question-search",
        },
        {
            id: "pdf-editor",
            name: "PDF Editor",
            icon: FaFilePdf,
            link: "/pdf-editor",
        },
    ]

    const handleToolClick = (tool) => {
        if (tool.onClick) {
            tool.onClick()
        } else if (tool.link) {
            navigate(tool.link)
        } else if (tool.path) {
            navigate(tool.path)
        }
    }

    const handleSearchSubmit = () => {
        if (query.trim()) {
            navigate("/chatbox", { state: { questionPrompt: query } })
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault()
            handleSearchSubmit()
        }
    }

    return (
        <>
            <Helmet>
                {/* 1. CMP */}
                <script
                    id="usercentrics-cmp"
                    src="https://app.usercentrics.eu/browser-ui/latest/loader.js"
                    data-settings-id="R7Xi1xeFRKhrKk"
                    async
                />
                {/* 2. gtag */}
                <script
                    async
                    src="https://www.googletagmanager.com/gtag/js?id=AW-17303305286"
                />
                <script>{`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'AW-17303305286');
      `}</script>
                {/* 3a. GTM head */}
                <script>{`
        (function(w,d,s,l,i){
          w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
          var f=d.getElementsByTagName(s)[0], j=d.createElement(s), dl=l!='dataLayer'?'&l='+l:'';
          j.async=true; j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
          f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-MBKBFC9M');
      `}</script>
                {/* Browser tab title */}
                <title>Exam Crusher | AIAT</title>

                {/* Basic SEO */}
                <meta
                    name="description"
                    content="AIAT’s Exam Crusher helps you master A-level, AP, and IB exams with AI-powered past papers, step-by-step solutions, and scoring strategies."
                />
                <meta
                    name="keywords"
                    content="A-level, AP, IB, exam prep, AI tutor, past papers, step-by-step solutions"
                />

                {/* Favicon */}
                <link rel="icon" href="/favicon.ico" />

                {/* Open Graph for Facebook, LinkedIn, etc. */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Exam Crusher | AIAT" />
                <meta
                    property="og:description"
                    content="AI-powered exam prep for A-level, AP, and IB: practice past papers, detailed solutions, and scoring strategies."
                />
                <meta property="og:url" content={window.location.href} />


                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Exam Crusher | AIAT" />
                <meta
                    name="twitter:description"
                    content="AI-powered exam prep for A-level, AP, and IB: practice past papers, detailed solutions, and scoring strategies."
                />
                <meta
                    name="twitter:image"
                    content="https://yourdomain.com/assets/social-homepage.png"
                />
            </Helmet>
            <div className="App min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 relative overflow-hidden">
                {/* Animated Background Elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    {/* Floating Orbs */}
                    <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-blue-400/20 to-indigo-600/20 rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute top-40 right-20 w-96 h-96 bg-gradient-to-r from-red-400/20 to-red-600/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
                    <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-gradient-to-r from-emerald-400/20 to-cyan-600/20 rounded-full blur-3xl animate-pulse delay-2000"></div>

                {/* Animated Grid */}
                <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px] animate-pulse"></div>

                {/* Mouse Follower */}
                <div
                    className="absolute w-96 h-96 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 rounded-full blur-3xl transition-all duration-1000 ease-out"
                    style={{
                        left: mousePosition.x - 192,
                        top: mousePosition.y - 192,
                    }}
                ></div>
            </div>

            {/* Main Content */}
            <main className="flex-grow flex flex-col relative z-10">
                <div className="container mx-auto px-4 py-8">
                    {/* Hero Section with Image */}
                    <div
                        className={`mb-20 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                    >
                        <div className="max-w-6xl mx-auto text-center mb-3 relative">



                            <div className="pt-2">
                                <h1 className="text-8xl font-black text-white mb-6 relative">
                                    <span className="absolute inset-0 bg-gradient-to-br from-slate-500 to-slate-600 bg-clip-text text-transparent transform translate-x-2 translate-y-2 blur-sm">
                                        ExamCrusher
                                    </span>
                                    <span className="absolute inset-0 bg-gradient-to-br from-slate-400 to-slate-500 bg-clip-text text-transparent transform translate-x-1 translate-y-1">
                                        ExamCrusher
                                    </span>
                                    <span className="relative z-10 text-white drop-shadow-2xl">ExamCrusher</span>
                                </h1>
                                <div className="flex items-center justify-center space-x-2 mb-2">
                                    <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-lg rounded-full px-6 py-3 border border-white/20">
                                        <FaStar className="text-yellow-400 animate-spin" />
                                        <span className="text-xl text-white font-semibold">A-Level</span>
                                    </div>
                                    <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-lg rounded-full px-6 py-3 border border-white/20">
                                        <FaRocket className="text-blue-400 animate-bounce" />
                                        <span className="text-xl text-white font-semibold">IB</span>
                                    </div>
                                    <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-lg rounded-full px-6 py-3 border border-white/20">
                                        <FaGraduationCap className="text-indigo-400 animate-pulse" />
                                        <span className="text-xl text-white font-semibold">AP</span>
                                    </div>
                                </div>
                                <p className="text-2xl text-slate-300 font-medium">Exam Prep with AI Functions</p>
                            </div>
                        </div>

                        {/* Enhanced Search Bar */}
                        <div className="max-w-4xl mx-auto">
                            <div className="relative group">
                                <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-indigo-600 to-red-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-tilt"></div>
                                <div className="relative flex items-center gap-4 bg-slate-900/90 backdrop-blur-xl rounded-2xl p-4 border border-white/20">
                                    <div className="flex items-center flex-1 bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden">
                                        <div className="p-4">
                                            <FaSearch className="text-slate-500 text-xl animate-pulse" />
                                        </div>
                                        <input
                                            type="text"
                                            className="flex-1 p-4 text-lg bg-transparent border-none outline-none text-slate-800 placeholder-slate-500"
                                            placeholder="What do you need help with?"
                                            value={query}
                                            onChange={(e) => setQuery(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                        />
                                    </div>
                                    <button
                                        onClick={handleSearchSubmit}
                                        className="relative bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-12 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-2xl hover:shadow-blue-500/25 transform hover:scale-105 group overflow-hidden"
                                    >
                                        <span className="relative z-10">Try now</span>
                                        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Layout - Left 2/3, Right 1/3 */}
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-auto lg:h-[900px]">
                            {/* Left Column - Main Functions (2/3 width) */}
                            <div
                                className={`lg:col-span-2 transition-all duration-1000 delay-300 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}
                            >
                                <div className="flex items-center space-x-4 mb-8">
                                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                                        <FaRocket className="text-white text-2xl animate-bounce" />
                                    </div>
                                    <h2 className="text-5xl font-bold text-white">Main Functions</h2>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-[200px]">
                                    {mainFunctions.map((tool, index) => (
                                        <div
                                            key={tool.name}
                                            onClick={() => handleToolClick(tool)}
                                            className={`group relative bg-slate-800/40 backdrop-blur-xl p-8 rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-500 cursor-pointer transform hover:scale-105 hover:-translate-y-3 border border-white/10 hover:border-white/30 overflow-hidden animate-fade-in-up flex flex-col justify-center min-h-[350px]`}
                                            style={{ animationDelay: `${index * 200}ms` }}
                                        >
                                            {/* Animated Background Gradient */}
                                            <div
                                                className={`absolute inset-0 bg-gradient-to-br ${tool.gradient} opacity-0 group-hover:opacity-20 transition-all duration-500 rounded-3xl`}
                                            ></div>

                                            {/* Shimmer Effect */}
                                            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

                                            <div className="relative z-10 text-center">
                                                <div
                                                    className={`bg-gradient-to-br ${tool.gradient} p-8 rounded-3xl shadow-xl group-hover:shadow-2xl transition-all duration-300 group-hover:rotate-12 group-hover:scale-110 mx-auto mb-8 w-fit`}
                                                >
                                                    <tool.icon className="text-6xl text-white" />
                                                </div>

                                                <h3 className="text-4xl font-bold text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-indigo-400 group-hover:bg-clip-text transition-all duration-300 mb-4">
                                                    {tool.name}
                                                </h3>

                                                <p className="text-slate-400 text-xl leading-relaxed group-hover:text-slate-300 transition-colors duration-300">
                                                    {tool.description}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Right Column - Advanced Tools (Top) + Utility Tools (Bottom) */}
                            <div
                                className={`lg:col-span-1 flex flex-col gap-8 transition-all duration-1000 delay-500 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}
                            >
                                {/* Advanced Tools - Top Half */}
                                <div className="flex-1">
                                    <div className="flex items-center space-x-3 mb-6">
                                        <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-cyan-600 rounded-full flex items-center justify-center">
                                            <FaStar className="text-white text-lg animate-spin" />
                                        </div>
                                        <h2 className="text-3xl font-bold text-white">Advanced Tools</h2>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 h-full content-start">
                                        {advancedTools.map((tool, index) => (
                                            <div
                                                key={tool.name}
                                                onClick={() => handleToolClick(tool)}
                                                className={`group relative bg-slate-800/30 backdrop-blur-xl p-4 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer transform hover:scale-105 hover:-translate-y-2 border border-white/10 hover:border-indigo-400/50 overflow-hidden animate-fade-in-up flex flex-col items-center justify-center text-center min-h-[120px]`}
                                                style={{ animationDelay: `${600 + index * 100}ms` }}
                                            >
                                                {/* Hover Background */}
                                                <div
                                                    className={`absolute inset-0 bg-gradient-to-br ${tool.gradient} opacity-0 group-hover:opacity-20 transition-all duration-500 rounded-2xl`}
                                                ></div>

                                                {/* Glow Effect */}
                                                <div
                                                    className={`absolute -inset-1 bg-gradient-to-r ${tool.gradient} rounded-2xl opacity-0 group-hover:opacity-20 blur transition-all duration-500`}
                                                ></div>

                                                <div className="relative z-10">
                                                    <div
                                                        className={`bg-gradient-to-br ${tool.gradient} p-3 rounded-xl shadow-lg transition-all duration-300 group-hover:rotate-12 group-hover:scale-110 mx-auto mb-3 w-fit`}
                                                    >
                                                        <tool.icon className="text-2xl text-white" />
                                                    </div>

                                                    <h3 className="text-sm font-bold text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-indigo-400 group-hover:to-red-400 group-hover:bg-clip-text transition-all duration-300 mb-1">
                                                        {tool.name}
                                                    </h3>

                                                    <p className="text-slate-400 text-xs leading-tight group-hover:text-slate-300 transition-colors duration-300 line-clamp-2">
                                                        {tool.description}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Utility Tools - Bottom Half */}
                                <div className="flex-1">
                                    <div className="flex items-center space-x-3 mb-6">
                                        <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-rose-600 rounded-full flex items-center justify-center">
                                            <FaBrain className="text-white text-sm animate-pulse" />
                                        </div>
                                        <h2 className="text-2xl font-bold text-white">Utility Tools</h2>
                                    </div>

                                    <div className="bg-slate-800/20 backdrop-blur-xl rounded-2xl p-6 border border-white/10 h-full">
                                        <div className="grid grid-cols-2 gap-3 h-full content-start">
                                            {utilityFunctions.map((tool, index) => (
                                                <div
                                                    key={tool.id}
                                                    onClick={() => handleToolClick(tool)}
                                                    className={`group relative bg-slate-700/40 backdrop-blur-lg p-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-105 hover:-translate-y-1 border border-white/10 hover:border-slate-400/30 overflow-hidden animate-fade-in-up`}
                                                    style={{ animationDelay: `${800 + index * 50}ms` }}
                                                >
                                                    {/* Hover Background */}
                                                    <div className="absolute inset-0 bg-gradient-to-br from-slate-600/30 to-slate-700/30 opacity-0 group-hover:opacity-100 transition-all duration-300 rounded-xl"></div>

                                                    <div className="relative z-10 text-center">
                                                        <div className="bg-gradient-to-br from-slate-600 to-slate-700 group-hover:from-slate-500 group-hover:to-slate-600 p-3 rounded-lg shadow-md mx-auto mb-3 w-fit transition-all duration-300 group-hover:scale-110">
                                                            <tool.icon className="text-lg text-white" />
                                                        </div>

                                                        <h3 className="text-xs font-semibold text-white group-hover:text-slate-200 transition-colors duration-300 leading-tight">
                                                            {tool.name}
                                                        </h3>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>



                </div>
            </main>

        {showLecturePopup && (
          <LatexGenerator
            latexCodeSent={`
              ------------------------
              Quantum Physics
              1. Wave-Particle Duality
              2. Superposition Principle
              3. Quantum Entanglement
              4. Heisenberg Uncertainty Principle
              5. Quantum Tunneling
              ------------------------`}
            onClose={() => setShowLecturePopup(false)}
          />
        )}

        {showSlidesPopup && (
          <SlidesGenerator
            latexCodeSent={`
              ------------------------
              Quantum Physics
              1. Wave-Particle Duality
              2. Superposition Principle
              3. Quantum Entanglement
              4. Heisenberg Uncertainty Principle
              5. Quantum Tunneling
              ------------------------`}
            onClose={() => setSlidesPopup(false)}
          />
        )}

        {(showLecturePopup || showSlidesPopup) && <div className="blur-background"></div>}


            <div className="mt-20 relative z-10">
                <MathPlatformIntro />
            </div>

            <footer className="bg-slate-900/80 backdrop-blur-xl text-white py-12 mt-16 border-t border-white/10 relative z-10">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div>
                            <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                                Quick Links
                            </h3>
                            <ul className="space-y-3">
                                {[
                                    { name: "Home", href: "/" },
                                    { name: "Explore Calculus", href: "https://tutorial.math.lamar.edu/" },
                                    { name: "AITE Home", href: "https://www.aiteinstitute.com/ch/index.html" },
                                    { name: "Past Papers", href: "https://pastpapers.co/" },
                                    { name: "MATH Competition", href: "https://artofproblemsolving.com/" },
                                    { name: "MATH tools", href: "https://www.wolframalpha.com/" },
                                    { name: "Textbook Finder", href: "https://www.libgen.is/" },
                                ].map((link, index) => (
                                    <li key={index}>
                                        <a
                                            href={link.href}
                                            className="text-slate-400 hover:text-blue-400 transition-all duration-300 hover:translate-x-2 inline-block"
                                            target={link.href.startsWith("http") ? "_blank" : "_self"}
                                            rel={link.href.startsWith("http") ? "noreferrer" : ""}
                                        >
                                            {link.name}
                                            {link.href.startsWith("http") && <i className="fas fa-external-link-alt ml-2 text-xs"></i>}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-indigo-400 to-red-400 bg-clip-text text-transparent">
                                Legal & Privacy
                            </h3>
                            <ul className="space-y-3">
                                {[
                                    { name: "Privacy Policy", href: "/privacy-policy" },
                                    { name: "Terms of Service", href: "/terms" },
                                    { name: "Cookie Policy", href: "/cookie-policy" },
                                    { name: "Upload", href: "/questions/edit" },
                                    { name: "Convert Image", href: "/image-text-convertor" },
                                    { name: "Latex Editor", href: "/latex-editor" },
                                ].map((link, index) => (
                                    <li key={index}>
                                        <a
                                            href={link.href}
                                            className="text-slate-400 hover:text-indigo-400 transition-all duration-300 hover:translate-x-2 inline-block"
                                        >
                                            {link.name}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                                Contact Us
                            </h3>
                            <ul className="space-y-3 mb-8">
                                <li>
                                    <a
                                        href="mailto:info@aiteinstitute.com"
                                        className="text-slate-400 hover:text-emerald-400 transition-all duration-300 hover:translate-x-2 inline-block"
                                    >
                                        Email: info@aiteinstitute.com
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="https://www.aiteinstitute.com/en/about/"
                                        className="text-slate-400 hover:text-emerald-400 transition-all duration-300 hover:translate-x-2 inline-block"
                                    >
                                        Help Center
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="https://www.aiteinstitute.com/ch/lxwm/"
                                        className="text-slate-400 hover:text-emerald-400 transition-all duration-300 hover:translate-x-2 inline-block"
                                    >
                                        Leave Message
                                    </a>
                                </li>
                            </ul>

                            <div>
                                <h4 className="text-xl font-bold mb-4 text-cyan-400">Follow Us</h4>
                                <div className="flex space-x-4">
                                    {[
                                        { icon: "fab fa-facebook-f", href: "https://facebook.com", color: "hover:text-blue-400" },
                                        { icon: "fab fa-twitter", href: "https://twitter.com", color: "hover:text-cyan-400" },
                                        { icon: "fab fa-linkedin-in", href: "https://linkedin.com", color: "hover:text-blue-600" },
                                        { icon: "fab fa-instagram", href: "https://instagram.com", color: "hover:text-red-400" },
                                    ].map((social, index) => (
                                        <a
                                            key={index}
                                            href={social.href}
                                            target="_blank"
                                            className={`text-slate-400 ${social.color} transition-all duration-300 text-2xl transform hover:scale-125 hover:-translate-y-1`}
                                            rel="noreferrer"
                                        >
                                            <i className={social.icon}></i>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>

            <style jsx>{`
        @keyframes gradient-x {
          0%, 100% {
            background-size: 200% 200%;
            background-position: left center;
          }
          50% {
            background-size: 200% 200%;
            background-position: right center;
          }
        }
        
        @keyframes tilt {
          0%, 50%, 100% {
            transform: rotate(0deg);
          }
          25% {
            transform: rotate(1deg);
          }
          75% {
            transform: rotate(-1deg);
          }
        }
        
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-gradient-x {
          animation: gradient-x 3s ease infinite;
        }
        
        .animate-tilt {
          animation: tilt 10s infinite linear;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
          opacity: 0;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
            </div>
        </>
    )
}

export default Home
